/**
 * Content freshness engine — the sole content emitter of the media-page hook
 * DAG.
 *
 * Fetches the media page with the metadata columns the subscribers need
 * (`output_state` — the media_output_workflow state — plus the subtitle
 * metadata fields) and exposes the result as `enrichedContent`. Downstream
 * hooks (player sources, processing) are pure subscribers of this content.
 *
 * Timing:
 *   - Always fetches once, even without a trigger — so a cold page load (e.g.
 *     everything already terminal, no upload/generation) still gets enriched
 *     content, which is what the failed-items notice depends on.
 *   - When a trigger fires (upload `?process-completed=true` or subtitle
 *     generation), it polls on the schedule 0s,1s,2s,3s,4s,5s,5s... until all
 *     transcoding/subtitle items are terminal (after a 10s grace period), or
 *     the 10min safety net.
 *
 * The enriched fetch is a plain GET on the content path with `metadata_fields`
 * (works for anonymous users on unlisted pages — the backend allows depth=1
 * children of an unlisted parent).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Api from '@plone/volto/helpers/Api/Api';
import {
  GRACE_PERIOD_MS,
  MAX_POLL_DURATION_MS,
  ALLOWED_TYPES,
  ITEM_METADATA_FIELDS,
  getInterval,
  isTerminalState,
} from 'volto-base-addon/helpers/contentFreshnessLogic';

// Fetch the media page enriched with the metadata columns. Never throws —
// returns null on error so the poll loop keeps going.
export const fetchEnrichedContent = async (contentPath, token, api) => {
  try {
    const qs = ITEM_METADATA_FIELDS.map(
      (f) => `metadata_fields=${encodeURIComponent(f)}`,
    ).join('&');
    return await api.get(`${contentPath}?${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    return null;
  }
};

// True when every transcoding/subtitle item has reached a terminal
// media_output_workflow state (read straight from the enriched metadata).
const allItemsTerminal = (content) => {
  const items = (Array.isArray(content?.items) ? content.items : []).filter(
    (i) => ALLOWED_TYPES.includes(i['@type']),
  );
  if (items.length === 0) return false;
  return items.every((i) => isTerminalState(i.output_state));
};

export const useContentFreshness = (contentPath, { trigger }) => {
  const { token } = useSelector((state) => state.userSession);

  const [enrichedContent, setEnrichedContent] = useState(null);

  const apiRef = useRef(new Api());
  const tokenRef = useRef(token);
  const timerRef = useRef(null);
  // Each effect invocation increments this. Callbacks compare their captured
  // generation against the current value to detect staleness. This is the sole
  // mechanism that prevents overlapping invocations from interfering.
  const generationRef = useRef(0);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const runFetch = useCallback(async () => {
    const result = await fetchEnrichedContent(
      contentPath,
      tokenRef.current,
      apiRef.current,
    );
    if (result) {
      setEnrichedContent(result);
    }
    return result;
  }, [contentPath]);

  useEffect(() => {
    if (!contentPath) return;

    generationRef.current += 1;
    const gen = generationRef.current;
    const startTime = Date.now();
    let pollCount = 0;
    let cancelled = false;

    const isStale = () => cancelled || generationRef.current !== gen;

    const poll = async () => {
      if (isStale()) return;

      const result = await runFetch();

      if (isStale()) return;

      // Without a trigger we only enrich once — no polling.
      if (!trigger) return;

      // After the grace period, stop once everything is terminal.
      const elapsed = Date.now() - startTime;
      if (elapsed > GRACE_PERIOD_MS && allItemsTerminal(result)) return;

      // Safety net.
      if (elapsed > MAX_POLL_DURATION_MS) return;

      // Schedule next poll.
      pollCount += 1;
      timerRef.current = setTimeout(poll, getInterval(pollCount));
    };

    // First fetch at interval 0 (immediate via setTimeout to stay async).
    timerRef.current = setTimeout(poll, getInterval(0));

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [trigger, contentPath, runFetch]);

  return { enrichedContent };
};

export default useContentFreshness;
